import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";
import { publicApi } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import { Calendar, MapPin, Trophy, ArrowRight } from "lucide-react";

/**
 * HIBRIT — Magazine-style: news on left, match center on right.
 * Mixes journalism vibe with live data.
 */
export default function HibritTemplate() {
    const { branding } = useBranding();
    const [data, setData] = useState({ next: null, last: null, players: [], posts: [], standings: [], matches: [] });

    useEffect(() => {
        Promise.all([
            publicApi.nextMatch().catch(() => null),
            publicApi.lastMatch().catch(() => null),
            publicApi.players().catch(() => []),
            publicApi.posts({ limit: 8 }).catch(() => []),
            publicApi.standings().catch(() => []),
            publicApi.matches({ limit: 10 }).catch(() => []),
        ]).then(([next, last, players, posts, standings, matches]) =>
            setData({ next, last, players, posts, standings, matches })
        );
    }, []);

    const primary = branding.primary_color || "#f5dc4c";
    const lead = data.posts[0];
    const rest = data.posts.slice(1, 7);

    return (
        <PublicLayout>
            <div data-testid="template-hibrit" className="bg-[#f5f5f0] text-[#0a0a0a]">
                {/* Magazine masthead */}
                <section className="border-b-4 border-black bg-white">
                    <div className="container-x py-6 flex items-center justify-between">
                        <div className="text-2xl font-extrabold uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                            {branding.name}
                        </div>
                        <div className="text-xs uppercase tracking-widest text-neutral-500">{new Date().toLocaleDateString("tr-TR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
                    </div>
                </section>

                {/* Two-column layout */}
                <section className="container-x py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
                    {/* Left: news lead + grid */}
                    <div className="lg:col-span-2">
                        {lead && (
                            <Link to={`/haberler/${lead.slug}`} className="block group mb-10">
                                <div className="aspect-[16/9] bg-neutral-200 mb-4 overflow-hidden">
                                    {lead.cover_image && <img src={lead.cover_image} alt={lead.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
                                </div>
                                <div className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: primary, backgroundColor: "#000", padding: "0.25rem 0.5rem", display: "inline-block" }}>
                                    MANŞET · {lead.category}
                                </div>
                                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight group-hover:underline" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{lead.title}</h2>
                                <p className="text-neutral-600 mt-3 text-lg line-clamp-2">{lead.excerpt || lead.summary || ""}</p>
                            </Link>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t-2 border-black pt-8">
                            {rest.map((p) => (
                                <Link key={p.id} to={`/haberler/${p.slug}`} className="group">
                                    <div className="aspect-[4/3] bg-neutral-200 mb-3 overflow-hidden">
                                        {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>}
                                    </div>
                                    <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">{p.category}</div>
                                    <h3 className="font-bold group-hover:underline" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.25rem" }}>{p.title}</h3>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Right: match center */}
                    <aside className="space-y-6">
                        {data.next && (
                            <div className="bg-black text-white p-6">
                                <div className="text-xs uppercase tracking-widest mb-2" style={{ color: primary }}>SIRADAKİ MAÇ</div>
                                <div className="text-2xl font-bold mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{data.next.home_team} vs {data.next.away_team}</div>
                                <div className="flex items-center gap-3 text-sm text-neutral-300">
                                    <Calendar size={14}/>{(data.next.match_date || "").slice(0, 10)}
                                </div>
                                {data.next.venue && <div className="flex items-center gap-3 text-sm text-neutral-300 mt-1"><MapPin size={14}/>{data.next.venue}</div>}
                            </div>
                        )}
                        {data.last && (
                            <div className="bg-white border-2 border-black p-6">
                                <div className="text-xs uppercase tracking-widest mb-2" style={{ color: primary }}>SON SONUÇ</div>
                                <div className="text-2xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{data.last.home_team} {data.last.home_score}-{data.last.away_score} {data.last.away_team}</div>
                                <div className="text-xs text-neutral-500">{(data.last.match_date || "").slice(0, 10)}</div>
                            </div>
                        )}
                        {data.standings.length > 0 && (
                            <div className="bg-white border-2 border-black p-6">
                                <div className="text-xs uppercase tracking-widest mb-3 flex items-center gap-2" style={{ color: primary }}><Trophy size={14}/> PUAN DURUMU (İlk 5)</div>
                                <div className="space-y-2 text-sm">
                                    {data.standings.slice(0, 5).map((s) => (
                                        <div key={s.id} className="flex items-center justify-between">
                                            <span><span className="font-bold mr-2">{s.rank}.</span>{s.team_name}</span>
                                            <span className="font-mono font-bold">{s.points}</span>
                                        </div>
                                    ))}
                                </div>
                                <Link to="/puan-durumu" className="block mt-3 text-xs uppercase tracking-widest font-bold hover:underline">Tam tablo <ArrowRight size={12} className="inline"/></Link>
                            </div>
                        )}
                    </aside>
                </section>
            </div>
        </PublicLayout>
    );
}
